/**
 * Suno Service
 *
 * Client-side service for playlist generation via the ShowLark extension.
 * All generation happens in the extension - this service just provides
 * a clean API for the Visualizer to use.
 *
 * The extension handles:
 * - Suno API calls (tokens never leave the browser)
 * - Task persistence and resumption
 * - Progress tracking
 */

import { extensionBridge } from './extension-bridge.js';

/**
 * Suno service for playlist generation
 */
export const sunoService = {
  /**
   * Check if the extension is available
   * @returns {Promise<boolean>}
   */
  async isExtensionAvailable() {
    try {
      return await extensionBridge.isAvailable();
    } catch (e) {
      return false;
    }
  },

  /**
   * Get extension version
   * @returns {string|null}
   */
  getExtensionVersion() {
    return extensionBridge.getVersion();
  },

  /**
   * Get current connection status
   * @returns {{ status: string, details: object, connected: boolean }}
   */
  getConnectionStatus() {
    return extensionBridge.getConnectionStatus();
  },

  /**
   * Register a callback for connection status changes
   * @param {function} callback - Called with (status, details, connected)
   * @returns {function} - Cleanup function to unregister the callback
   */
  onConnectionStatusChange(callback) {
    return extensionBridge.onConnectionStatusChange(callback);
  },

  /**
   * Check if connection is currently healthy
   * @returns {boolean}
   */
  isConnected() {
    return extensionBridge.isConnected();
  },

  /**
   * Check availability and return reason if unavailable
   * @returns {Promise<{ available: boolean, reason?: string, action?: string }>}
   */
  async checkAvailability() {
    // Check if extension is installed
    const extensionAvailable = await this.isExtensionAvailable();
    if (!extensionAvailable) {
      return {
        available: false,
        reason: 'ShowLark extension not installed',
        action: 'Please install the ShowLark extension'
      };
    }

    // Check if connection is healthy
    const connectionStatus = this.getConnectionStatus();
    if (!connectionStatus.connected) {
      return {
        available: false,
        reason: connectionStatus.details?.reason || 'Extension connection lost',
        action: connectionStatus.details?.action || 'Please refresh the page'
      };
    }

    return { available: true };
  },

  /**
   * Check if there's a valid Suno token in the extension
   * @returns {Promise<{hasToken: boolean, isValid: boolean, expiresAt: number|null}>}
   */
  async getTokenStatus() {
    try {
      return await extensionBridge.getTokenStatus();
    } catch (e) {
      return { hasToken: false, isValid: false, expiresAt: null };
    }
  },

  /**
   * Get formatted token expiry info
   * @returns {Promise<{expired: boolean, remaining: number, formatted: string}|null>}
   */
  async getTokenExpiry() {
    const status = await this.getTokenStatus();

    if (!status.hasToken || !status.expiresAt) {
      return null;
    }

    const now = Date.now();
    const remaining = status.expiresAt - now;

    if (remaining <= 0) {
      return { expired: true, remaining: 0, formatted: 'Expired' };
    }

    const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
    const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));

    return {
      expired: false,
      remaining,
      days,
      hours,
      formatted: days > 0 ? `${days}d ${hours}h` : `${hours}h`
    };
  },

  /**
   * Start playlist generation
   * @param {object} options - Generation options
   * @param {string} options.genre - Music genre
   * @param {string} options.theme - Optional theme
   * @param {number} options.songCount - Number of songs (1-100, subject to tier limits)
   * @param {string} options.audioType - 'instrumental' or 'vocal'
   * @param {boolean} options.fixedPrompt - Use same prompt for all songs (default: false)
   * @param {boolean} options.fixedLyrics - Use same lyrics for all songs (default: false)
   * @returns {Promise<{taskId: string, status: string}>}
   */
  async generatePlaylist(options) {
    const { genre, theme, songCount, audioType, fixedPrompt, fixedLyrics } = options;

    // Validate
    if (!genre) {
      throw new Error('Genre is required');
    }

    // Check extension
    const available = await this.isExtensionAvailable();
    if (!available) {
      throw new Error('ShowLark extension required. Please install the extension to generate playlists.');
    }

    // Start generation - validate song count (1-100, tier limits enforced by extension)
    const validatedSongCount = Math.max(1, Math.min(100, songCount || 10));

    const result = await extensionBridge.startGeneration({
      genre,
      theme: theme || '',
      songCount: validatedSongCount,
      audioType: audioType || 'instrumental',
      fixedPrompt: Boolean(fixedPrompt),
      fixedLyrics: Boolean(fixedLyrics)
    });

    return {
      taskId: result.task.id,
      status: result.task.status
    };
  },

  /**
   * Get current task status
   * @returns {Promise<object|null>}
   */
  async getTaskStatus() {
    try {
      return await extensionBridge.getTaskStatus();
    } catch (e) {
      return null;
    }
  },

  /**
   * Set up progress callback
   * @param {function} callback - Called with task updates
   */
  onProgress(callback) {
    extensionBridge.onProgress(callback);
  },

  /**
   * Clear progress callback
   */
  clearProgress() {
    extensionBridge.clearProgressCallback();
  },

  /**
   * Cancel current generation
   * @returns {Promise<void>}
   */
  async cancelGeneration() {
    await extensionBridge.cancelGeneration();
  },

  /**
   * Resume generation after CAPTCHA
   * @returns {Promise<{taskId: string}>}
   */
  async resumeAfterCaptcha() {
    const result = await extensionBridge.resumeAfterCaptcha();
    return { taskId: result.task.id };
  },

  // =========================================================================
  // Library Browsing (Suno -> Visualizer)
  // =========================================================================

  /**
   * Browse user's Suno library (v3 cursor-based pagination)
   * @param {object} options - Pagination and filter options
   * @param {string|null} options.cursor - Cursor for pagination (null for first page)
   * @param {number} options.pageSize - Items per page (default 20)
   * @param {string|null} options.searchText - Server-side text search query
   * @param {string} options.filter - Filter type: 'all', 'liked', 'created'
   * @returns {Promise<{songs: object[], hasMore: boolean, total: number, nextCursor: string|null}>}
   */
  async browseLibrary(options = {}) {
    // Check extension
    const available = await this.isExtensionAvailable();
    if (!available) {
      throw new Error('ShowLark extension required. Please install the extension to browse your Suno library.');
    }

    return await extensionBridge.browseLibrary(options);
  },

  /**
   * Search user's Suno library (delegates to browseLibrary with searchText)
   * @param {string} query - Search query
   * @param {object} options - Pagination options
   * @returns {Promise<{songs: object[], hasMore: boolean, nextCursor: string|null}>}
   */
  async searchLibrary(query, options = {}) {
    return this.browseLibrary({ ...options, searchText: query });
  },

  /**
   * Get single song metadata
   * @param {string} clipId - The clip ID
   * @returns {Promise<object>} Song metadata
   */
  async getSongMetadata(clipId) {
    const available = await this.isExtensionAvailable();
    if (!available) {
      throw new Error('ShowLark extension required.');
    }

    return await extensionBridge.getSongMetadata(clipId);
  },

  /**
   * Get aligned (time-synced) lyrics for a song
   * @param {string} clipId - The clip ID
   * @returns {Promise<object|null>} Aligned lyrics object or null if unavailable
   */
  async getAlignedLyrics(clipId) {
    const available = await this.isExtensionAvailable();
    if (!available) {
      return null;
    }

    return await extensionBridge.getAlignedLyrics(clipId);
  },

  // =========================================================================
  // Download/Export (Visualizer -> Filesystem)
  // =========================================================================

  /**
   * Get download URL for a song
   * Can be used to stream audio directly
   * @param {string} clipId - The clip ID
   * @param {string} format - 'mp3' or 'wav'
   * @returns {Promise<string>} The download URL
   */
  async getDownloadUrl(clipId, format = 'mp3') {
    const available = await this.isExtensionAvailable();
    if (!available) {
      throw new Error('ShowLark extension required.');
    }

    return await extensionBridge.getDownloadUrl(clipId, format);
  },

  /**
   * Export a single song to user's filesystem
   * @param {string} clipId - The clip ID
   * @param {string} format - 'mp3' or 'wav'
   * @param {string} filename - Optional filename (without extension)
   * @returns {Promise<{success: boolean, filename?: string}>}
   */
  async exportSong(clipId, format = 'mp3', filename) {
    const available = await this.isExtensionAvailable();
    if (!available) {
      throw new Error('ShowLark extension required.');
    }

    return await extensionBridge.exportSong(clipId, format, filename);
  },

  /**
   * Export multiple songs to user's filesystem
   * Subscribe to showlarkExportProgress event for progress updates
   * @param {Array<{clipId: string, title: string}>} songs - Songs to export
   * @param {string} format - 'mp3' or 'wav'
   * @returns {Promise<{success: boolean, completed: number, failed: number}>}
   */
  async exportBatch(songs, format = 'mp3') {
    const available = await this.isExtensionAvailable();
    if (!available) {
      throw new Error('ShowLark extension required.');
    }

    return await extensionBridge.exportBatch(songs, format);
  },

  /**
   * Set up export progress callback
   * @param {function} callback - Called with (current, total, song)
   */
  onExportProgress(callback) {
    const handler = (event) => {
      const { current, total, song } = event.detail;
      callback(current, total, song);
    };
    document.addEventListener('showlarkExportProgress', handler);
    // Return cleanup function
    return () => document.removeEventListener('showlarkExportProgress', handler);
  },

  // =========================================================================
  // Legacy token methods (deprecated - kept for backwards compatibility)
  // =========================================================================

  /**
   * @deprecated Token is now managed by extension
   */
  saveToken(token) {
    console.warn('[SunoService] saveToken() is deprecated - tokens are managed by the extension');
  },

  /**
   * @deprecated Token is now managed by extension
   */
  getToken() {
    console.warn('[SunoService] getToken() is deprecated - use getTokenStatus() instead');
    return null;
  },

  /**
   * @deprecated Token is now managed by extension
   */
  clearToken() {
    console.warn('[SunoService] clearToken() is deprecated - tokens are managed by the extension');
  }
};

// Listen for songs pushed from extension popup
document.addEventListener('showlarkPushSongs', (event) => {
  const { songs } = event.detail;
  console.log('[SunoService] Received songs from extension:', songs.length);

  // Dispatch event for playlist controller to handle
  document.dispatchEvent(new CustomEvent('playlistSongsReceived', {
    detail: { songs, source: 'extension' }
  }));
});

export default sunoService;
